import React, { useState } from "react";
import { MessageCircle, Phone, Mail, MapPin, Send } from "lucide-react";
import { WHATSAPP_NUMBER, WHATSAPP_DIGITS } from "../data/designOptions";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!form.name || !form.email || !form.message) {
      setStatus({ type: "error", text: "Please fill name, email and message." });
      return;
    }
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setStatus({ type: "ok", text: "Thank you — we'll reply within 24 hours." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (_) {
      setStatus({ type: "error", text: "Something went wrong. Please try WhatsApp." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[#0A0A0A] text-[#F7F5F0]"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32">
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#B8996F]">08 · Contact</span>
        <h2 className="mt-3 font-[Cormorant_Garamond] text-[14vw] font-light leading-[0.85] tracking-tight md:text-[8vw]">
          Let's <span className="italic text-[#B8996F]">talk.</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-[Outfit] text-base leading-relaxed text-white/70">
              Book a free studio visit, request a quote, or just send your inspiration board. Our senior designer will reply
              within one business day — usually faster.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`https://wa.me/${WHATSAPP_DIGITS}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group flex items-center gap-3 text-base text-white transition-colors duration-300 hover:text-[#25D366]"
              >
                <MessageCircle size={18} strokeWidth={1.6} className="text-[#25D366]" />
                <span>{WHATSAPP_NUMBER} · WhatsApp Quick Connect</span>
              </a>
              <a
                href="tel:+919739050448"
                data-testid="contact-phone"
                className="flex items-center gap-3 text-base text-white/85 transition-colors duration-300 hover:text-white"
              >
                <Phone size={18} strokeWidth={1.6} className="text-[#B8996F]" />
                <span>+91 97390 50448</span>
              </a>
              <a
                href="mailto:hello@designdecor.studio"
                data-testid="contact-email"
                className="flex items-center gap-3 text-base text-white/85 transition-colors duration-300 hover:text-white"
              >
                <Mail size={18} strokeWidth={1.6} className="text-[#B8996F]" />
                <span>hello@designdecor.studio</span>
              </a>
              <div className="flex items-start gap-3 text-base text-white/85">
                <MapPin size={18} strokeWidth={1.6} className="mt-1 text-[#B8996F]" />
                <span>
                  Design Decor Studio<br />
                  Shed #61, 2nd Gate, Gokul Road<br />
                  Industrial Estate<br />
				  Hubballi 580020, India
                </span>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="md:col-span-7"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  data-testid="contact-name"
                  className="mt-2 w-full border-b border-white/20 bg-transparent py-3 font-[Outfit] text-base text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                  data-testid="contact-email-input"
                  className="mt-2 w-full border-b border-white/20 bg-transparent py-3 font-[Outfit] text-base text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Phone (optional)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  data-testid="contact-phone-input"
                  className="mt-2 w-full border-b border-white/20 bg-transparent py-3 font-[Outfit] text-base text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">How can we help?</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  required
                  data-testid="contact-message"
                  className="mt-2 w-full resize-none border-b border-white/20 bg-transparent py-3 font-[Outfit] text-base text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                />
              </div>
            </div>

            {status && (
              <div
                data-testid="contact-status"
                className={`mt-5 border px-4 py-3 text-sm ${
                  status.type === "ok"
                    ? "border-[#B8996F]/40 bg-[#B8996F]/10 text-white"
                    : "border-red-400/40 bg-red-500/10 text-red-200"
                }`}
              >
                {status.text}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="mt-8 inline-flex items-center gap-2 bg-[#B8996F] px-6 py-4 text-[11px] uppercase tracking-[0.24em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F7F5F0] disabled:opacity-60"
            >
              <Send size={14} strokeWidth={1.8} />
              {submitting ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </div>
      </div>

      <footer className="border-t border-white/10 bg-[#0A0A0A]" data-testid="site-footer">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-3 px-6 py-6 text-[10px] uppercase tracking-[0.24em] text-white/45 md:flex-row md:items-center md:px-12">
          <span>© {new Date().getFullYear()} Design Decor Studio</span>
          <span>Crafted in Hubballi · India</span>
        </div>
      </footer>
    </section>
  );
};

export default Contact;
